import { DocumentChartBarIcon } from '@heroicons/react/20/solid';
import { WorksheetSchemaList, WorksheetSchemaSection } from '../../../../../types/custom-worksheet-v2';
import { formatListSummary, getFieldCount, isSection } from '../../store/utils';
import { useSelector } from 'react-redux';
import { selectSchema, selectShowHidden } from '../../store/selectors';
import { CWState } from '../../store/slice';
import { Progress } from '@cimplico/components';
import { getOffset, getZIndex } from '../../utils';
import EmptyStateNew from '../../../../../components/empty-state/EmptyStateNew';
import Section from '../section/Section';
import SectionGroup from '../section-group/SectionGroup';

type ListSummaryProps = {
    list: WorksheetSchemaList;
};

function ListSummary({ list }: ListSummaryProps): JSX.Element {
    const schema = useSelector((state: { schema: CWState }) => selectSchema({ state }));
    const showHidden = useSelector(selectShowHidden);

    const summary = formatListSummary(schema, list);

    let summaryFieldTotal = 0;
    let summaryFieldCompleted = 0;

    summary.forEach((s) => {
        const sectionFieldCount = getFieldCount(s, showHidden);
        summaryFieldCompleted = summaryFieldCompleted + sectionFieldCount.completed;
        summaryFieldTotal = summaryFieldTotal + sectionFieldCount.total;
    });

    // Calculate offset excluding the top padding above title (pt-8 = 32px)
    const offset = getOffset(`${list.id}.properties.summary`) - 32;
    const zIndex = getZIndex(`${list.id}.properties.summary`);

    function isHidden(property: WorksheetSchemaSection): boolean {
        return showHidden ? false : Boolean(property.properties[0].hidden);
    }

    return (
        <div className="scroll-mt-8 min-w-[500px] w-full relative">
            <div className="flex gap-2 items-center pt-8 pb-4 sticky w-full bg-white" style={{ zIndex, top: offset }}>
                <p className="text-gray-800 font-medium text-xl">Summary</p>
                <div className="flex gap-2 items-center">
                    <Progress
                        type="circle"
                        percent={summaryFieldTotal === 0 ? 100 : (summaryFieldCompleted / summaryFieldTotal) * 100}
                        size="small"
                    />
                    <p className="text-sm text-gray-600">
                        {summaryFieldCompleted}/{summaryFieldTotal}
                    </p>
                </div>
            </div>
            {summary.length > 0 ? (
                <div className="flex flex-col gap-8 pt-4">
                    {summary
                        .sort((a, b) => (a.order > b.order ? 1 : -1))
                        .map((property) => {
                            if (isSection(property)) {
                                if (isHidden(property)) {
                                    return <></>;
                                }
                                return <Section key={property.id} section={property} allowRedirect />;
                            }

                            return <SectionGroup key={property.id} sectionGroup={property} allowRedirect />;
                        })}
                </div>
            ) : (
                <div className="bg-white rounded-[5px] py-8 px-2 mt-3">
                    <EmptyStateNew icon={<DocumentChartBarIcon />} title={`No summary for ${list.name}`} />
                </div>
            )}
        </div>
    );
}

export default ListSummary;
